var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { action, makeObservable, observable } from 'mobx';
import { debounce } from 'throttle-debounce';
import { getImageSrcset, toHeaderSegment } from '../../utils';
import { ErrorUtil, pushGaData, SingleConcurrentRequestController } from '../../common';
const createNavEvent = (url) => {
    return {
        persist: () => null,
        preventDefault: () => null,
        currentTarget: new URL(url)
    };
};
const generateRandomString = () => {
    var _a;
    return ((_a = crypto === null || crypto === void 0 ? void 0 : crypto.randomUUID) === null || _a === void 0 ? void 0 : _a.call(crypto)) || Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};
export default class SearchStore {
    constructor(rootStore) {
        Object.defineProperty(this, "value", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ''
        });
        Object.defineProperty(this, "searchSuggestions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "dealerSearchSuggestions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "queryIds", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "formFilledGaEvent", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "dealerSuggestionsFetchStatus", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'UNINITIALIZED'
        });
        Object.defineProperty(this, "rootStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "searchSuggestionsController", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new SingleConcurrentRequestController()
        });
        Object.defineProperty(this, "dealerSuggestionsController", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new SingleConcurrentRequestController()
        });
        Object.defineProperty(this, "debouncedLoadSuggestions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "setSuggestionDebounce", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.debouncedLoadSuggestions = debounce(300, this.loadSuggestions);
            }
        });
        Object.defineProperty(this, "handleSubmit", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (event, customValue) => {
                var _a, _b, _c, _d, _e;
                event.preventDefault();
                this.searchSuggestionsController.cancelPreviousRequests();
                this.debouncedLoadSuggestions.cancel();
                const { app, URLData } = this.rootStore.environmentStore;
                const { setSection } = this.rootStore.sectionStore;
                const { setLocation } = this.rootStore.locationStore;
                const trimmedSubmitValue = (_a = (customValue || this.value)) === null || _a === void 0 ? void 0 : _a.trim();
                if (trimmedSubmitValue) {
                    if (((_b = event.currentTarget) === null || _b === void 0 ? void 0 : _b.getAttribute('type')) === 'button') {
                        pushGaData({ eventCategory: 'Search', eventAction: 'Popup', eventLabel: 'Link - Otsingu detailvaade' });
                    }
                    const query = encodeURIComponent(trimmedSubmitValue);
                    const searchPath = `${this.getSearchUrl()}?query=${query}`;
                    if (app === 'public') {
                        setLocation(createNavEvent(URLData.getPublicURL(searchPath)));
                    }
                    else if (app === 'shop') {
                        window.location.assign(URLData.getPublicURL(`${searchPath}&tab=pood`));
                    }
                    else if (app === 'online-help') {
                        window.location.assign(URLData.getPublicURL(`${searchPath}&tab=juhendid`));
                    }
                    else {
                        window.location.assign(URLData.getPublicURL(searchPath));
                    }
                }
                setSection(null);
                this.value = '';
                this.searchSuggestions = [];
                this.setSuggestionDebounce();
                (_c = document.getElementById('header-search-bar')) === null || _c === void 0 ? void 0 : _c.blur();
                const suggestionDropdown = (_d = document.getElementsByClassName('suggestion header__search-suggestion')) === null || _d === void 0 ? void 0 : _d.item(0);
                if (suggestionDropdown) {
                    (_e = suggestionDropdown.lastChild) === null || _e === void 0 ? void 0 : _e.remove();
                }
            }
        });
        Object.defineProperty(this, "handleChange", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (newValue) => {
                const { section } = this.rootStore.sectionStore;
                if (section === 'search') {
                    if (!this.formFilledGaEvent && newValue.length === 3) {
                        pushGaData({ eventCategory: 'Search', eventAction: 'Popup', eventLabel: 'Text Field Filled' });
                        this.formFilledGaEvent = true;
                    }
                    this.value = newValue;
                    this.debouncedLoadSuggestions(newValue);
                }
            }
        });
        Object.defineProperty(this, "loadSuggestions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (queryString) => {
                const { translate } = this.rootStore.translationsStore;
                const { lang, URLData } = this.rootStore.environmentStore;
                const trimmedQueryString = queryString === null || queryString === void 0 ? void 0 : queryString.trim();
                if (trimmedQueryString.length < 3) {
                    this.searchSuggestions = [];
                    return;
                }
                const url = this.rootStore.environmentStore.URLData.getSearchSuggestionsURL(encodeURIComponent(trimmedQueryString), this.getClientType());
                const response = await this.fetchJson(url);
                this.searchSuggestions = this.mapResults(response, lang, translate, URLData.shopBaseURL);
            }
        });
        Object.defineProperty(this, "loadDealerSearchSuggestions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                if (this.dealerSuggestionsFetchStatus === 'FETCHING') {
                    return;
                }
                this.dealerSuggestionsFetchStatus = 'FETCHING';
                try {
                    const response = await this.dealerSuggestionsController.get(this.rootStore.environmentStore.URLData.getDealerSearchSuggestionsURL(), {
                        withCredentials: true
                    });
                    this.dealerSuggestionsFetchStatus = 'FETCHED';
                    this.dealerSearchSuggestions.items = response.map((suggestion) => ({
                        title: suggestion,
                        url: this.rootStore.environmentStore.URLData.getPublicURL(`${this.getSearchUrl()}?query=${suggestion}`)
                    }));
                }
                catch (e) {
                    ErrorUtil.pushErrorToTeliaRum(e);
                    this.dealerSuggestionsFetchStatus = 'ERROR';
                    this.dealerSearchSuggestions.items = [];
                }
            }
        });
        Object.defineProperty(this, "fetchJson", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (url) => {
                try {
                    return await this.searchSuggestionsController.get(url, { withCredentials: true });
                }
                catch (error) {
                }
                return null;
            }
        });
        Object.defineProperty(this, "getClientType", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const { ssoStore: { authData: { customerType } }, locationStore: { segment } } = this.rootStore;
                if (customerType) {
                    return customerType === 'B2B' ? 'business' : 'private';
                }
                return toHeaderSegment(segment);
            }
        });
        Object.defineProperty(this, "handleSuggestionClick", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (item) => {
                const { setSection } = this.rootStore.sectionStore;
                const { onPathChange } = this.rootStore.externalCallbackStore;
                const type = this.resolveType(item.url);
                const algoliaAnalyticsData = this.getAlgoliaAnalyticsData(type, item);
                const searchParam = type === 'shop'
                    ? btoa(JSON.stringify({ ...algoliaAnalyticsData, positions: undefined, eventName: 'Shop search add to cart', eventType: 'conversion' }))
                    : undefined;
                pushGaData({ eventCategory: 'Search', eventAction: 'Popup', eventLabel: `Link - ${item.label}` });
                this.postSearchResultClickEvent(algoliaAnalyticsData);
                onPathChange(createNavEvent(searchParam ? `${item.url}?search=${searchParam}` : item.url));
                setSection(null);
            }
        });
        Object.defineProperty(this, "resolveType", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (url) => {
                const { URLData } = this.rootStore.environmentStore;
                if (url.startsWith(URLData.shopBaseURL)) {
                    return 'shop';
                }
                if (url.startsWith(URLData.onlineHelpBaseURL)) {
                    return 'oa';
                }
                return 'web';
            }
        });
        Object.defineProperty(this, "handleDealerSearchSuggestionClick", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (item) => {
                const { onPathChange } = this.rootStore.externalCallbackStore;
                const { setSection } = this.rootStore.sectionStore;
                setSection(null);
                this.value = '';
                onPathChange(createNavEvent(item.url));
            }
        });
        Object.defineProperty(this, "getAlgoliaAnalyticsData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (type, item) => {
                const { URLData } = this.rootStore.environmentStore;
                return {
                    eventName: `${type.charAt(0).toUpperCase() + type.slice(1)} search suggestion click`,
                    eventType: 'click',
                    userToken: generateRandomString(),
                    queryID: this.queryIds[type],
                    index: `${this.rootStore.environmentStore.tier}_${type}`,
                    objectIDs: ['shop', 'oa'].includes(type) ? [decodeURIComponent(item.url.split('/').pop())] : [item.url.split(URLData.publicBaseURL).pop()],
                    positions: [this.getSearchSuggestionItems(type).indexOf(item) + 1]
                };
            }
        });
        Object.defineProperty(this, "getSearchSuggestionItems", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (type) => {
                var _a;
                const { translate } = this.rootStore.translationsStore;
                return (((_a = this.searchSuggestions.find((suggestions) => suggestions.label === translate(`header.search.${type === 'shop' ? 'products' : type === 'oa' ? 'onlineHelp' : 'content'}`))) === null || _a === void 0 ? void 0 : _a.items) || []);
            }
        });
        Object.defineProperty(this, "postSearchResultClickEvent", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (event) => {
                try {
                    this.searchSuggestionsController.post(this.rootStore.environmentStore.URLData.getSearchEventURL(), event);
                }
                catch (e) {
                    ErrorUtil.pushErrorToTeliaRum(e);
                }
            }
        });
        Object.defineProperty(this, "getSearchUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const { segment } = this.rootStore.locationStore;
                const { authData } = this.rootStore.ssoStore;
                let searchSegment;
                const headerSegment = toHeaderSegment(segment, !authData.customerType);
                if (headerSegment) {
                    headerSegment === 'business' ? (searchSegment = 'ari') : (searchSegment = 'era');
                }
                if (authData.customerType && !headerSegment) {
                    authData.customerType === 'B2B' ? (searchSegment = 'ari') : (searchSegment = 'era');
                }
                return `/${searchSegment || 'era'}/otsing`;
            }
        });
        Object.defineProperty(this, "mapResults", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (searchSuggestionResults, lang, translate, shopBaseUrl) => {
                var _a, _b, _c;
                if (!searchSuggestionResults) {
                    return [];
                }
                const results = [];
                this.addShopResults(searchSuggestionResults, lang, results, translate, shopBaseUrl);
                this.addResults(searchSuggestionResults, results, translate, false);
                this.addResults(searchSuggestionResults, results, translate, true);
                this.queryIds = {
                    web: (_a = searchSuggestionResults.webResult) === null || _a === void 0 ? void 0 : _a.queryId,
                    shop: (_b = searchSuggestionResults.shopResult) === null || _b === void 0 ? void 0 : _b.queryId,
                    oa: (_c = searchSuggestionResults.oaResult) === null || _c === void 0 ? void 0 : _c.queryId
                };
                return results;
            }
        });
        Object.defineProperty(this, "addShopResults", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (searchSuggestionResults, lang, results, translate, shopBaseUrl) => {
                var _a, _b;
                if ((_b = (_a = searchSuggestionResults.shopResult) === null || _a === void 0 ? void 0 : _a.results) === null || _b === void 0 ? void 0 : _b.length) {
                    results.push({
                        label: translate('header.search.products'),
                        items: this.mapShopResults(searchSuggestionResults.shopResult.results, lang, shopBaseUrl)
                    });
                }
            }
        });
        Object.defineProperty(this, "addResults", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (searchSuggestionResults, results, translate, isWebResult) => {
                var _a;
                const suggestionResults = isWebResult ? searchSuggestionResults.webResult : searchSuggestionResults.oaResult;
                if ((_a = suggestionResults === null || suggestionResults === void 0 ? void 0 : suggestionResults.results) === null || _a === void 0 ? void 0 : _a.length) {
                    results.push({
                        label: isWebResult ? translate('header.search.content') : translate('header.search.onlineHelp'),
                        items: this.mapContentResults(suggestionResults.results, translate)
                    });
                }
            }
        });
        Object.defineProperty(this, "mapContentResults", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (results, translate) => {
                return results.map((result) => ({
                    ...result,
                    label: `${result.title}${result.segment ? ` - ${translate(`search.${result.segment}klient`)}` : ''}`
                }));
            }
        });
        Object.defineProperty(this, "mapShopResults", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (results, lang, shopBaseURL) => {
                return results.map((result) => {
                    var _a;
                    return ({
                        label: result.displayName[lang],
                        url: shopBaseURL + result.url,
                        image: { srcset: getImageSrcset(['24', '48'], ((_a = result.pictures) === null || _a === void 0 ? void 0 : _a.length) && result.pictures[0]), role: 'presentation' }
                    });
                });
            }
        });
        makeObservable(this);
        this.rootStore = rootStore;
        this.setSuggestionDebounce();
        this.dealerSearchSuggestions = {
            label: this.rootStore.translationsStore.translate('search.dealerSearchHistory'),
            items: []
        };
    }
}
__decorate([
    observable
], SearchStore.prototype, "value", void 0);
__decorate([
    observable
], SearchStore.prototype, "searchSuggestions", void 0);
__decorate([
    observable
], SearchStore.prototype, "dealerSearchSuggestions", void 0);
__decorate([
    observable
], SearchStore.prototype, "queryIds", void 0);
__decorate([
    observable
], SearchStore.prototype, "formFilledGaEvent", void 0);
__decorate([
    observable
], SearchStore.prototype, "dealerSuggestionsFetchStatus", void 0);
__decorate([
    action
], SearchStore.prototype, "handleSubmit", void 0);
__decorate([
    action
], SearchStore.prototype, "handleChange", void 0);
__decorate([
    action
], SearchStore.prototype, "loadSuggestions", void 0);
__decorate([
    action
], SearchStore.prototype, "loadDealerSearchSuggestions", void 0);
__decorate([
    action
], SearchStore.prototype, "handleSuggestionClick", void 0);
__decorate([
    action
], SearchStore.prototype, "handleDealerSearchSuggestionClick", void 0);
//# sourceMappingURL=SearchStore.js.map